import React, { useEffect, useState, useRef } from "react";
import { IoCloseOutline } from "react-icons/io5";
import TodoTab from "./TodoTab";
import { useDispatch, useSelector } from "react-redux";
import { setOpenTabs } from "../redux/opentab/opentabSlice";
import { create_or_update_opentab_thunk } from "../redux/opentab/opentabThunk";
import { isMacOS } from "../common/common";

const TabManager = ({
  openTabs,
  activeTab,
  setActiveTab,
  closeTodo,
  toggleTodoComplete,
  userId,
  sidebarInputRef,
  todoInputRef,
  todoEditInputRef,
  tabRenameInputRef,
  setShortCutFileRef,
  activeTabData,
  setActiveTabData,
}) => {
  const token = localStorage.getItem("todoToken");
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const tabsContainerRef = useRef(null);
  const shortcutsKeys = useSelector((state) => state.shortcut.shortcuts);
  const [tabInputValues, setTabInputValues] = useState({});
  const [isComposing, setIsComposing] = useState(false);

  const handleInputChange = (tabId, value) => {
    setTabInputValues((prev) => ({ ...prev, [tabId]: value }));
  };

  // Function to scroll to active tab
  const scrollToTab = (index) => {
    if (tabsContainerRef.current) {
      const tabElements = tabsContainerRef.current.children;
      if (tabElements[index]) {
        const tabElement = tabElements[index];
        const containerWidth = tabsContainerRef.current.offsetWidth;
        const tabOffsetLeft = tabElement.offsetLeft;
        const tabWidth = tabElement.offsetWidth;

        let scrollPosition = tabOffsetLeft + tabWidth / 2 - containerWidth / 2;
        scrollPosition = Math.max(
          0,
          Math.min(
            scrollPosition,
            tabsContainerRef.current.scrollWidth - containerWidth
          )
        );

        tabsContainerRef.current.scrollTo({
          left: scrollPosition,
          behavior: "auto",
        });
      }
    }
  };

  useEffect(() => {
    const activeIndex = openTabs?.findIndex((tab) => tab.id === activeTab);
    if (activeIndex !== -1) {
      scrollToTab(activeIndex);
    }
  }, [activeTab, openTabs]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") return;
    if (
      document.activeElement === sidebarInputRef?.current ||
      document.activeElement === todoInputRef?.current ||
      document.activeElement === todoEditInputRef?.current ||
      document.activeElement === tabRenameInputRef?.current ||
      document.activeElement === setShortCutFileRef?.current
    ) {
      if (
        (e.metaKey &&
          ["a", "c", "v", "z", "x"].includes(e.key.toLowerCase())) ||
        e.key === "Backspace" ||
        e.key === "Delete"
      ) {
        return;
      }
    }

    const currentIndex = openTabs?.findIndex((tab) => tab.id === activeTab);

    if (e.metaKey) {
      if (e.key >= "1" && e.key <= "9") {
        const numIndex = parseInt(e.key) - 1;
        if (numIndex < openTabs?.length) {
          setActiveTab(openTabs[numIndex].id);
          setActiveTabData(openTabs[numIndex]);
          scrollToTab(numIndex);
        }
        return;
      }
      if (e.code >= "Digit1" && e.code <= "Digit9") {
        const numIndex = parseInt(e.code.replace("Digit", "")) - 1;
        if (numIndex < openTabs?.length) {
          setActiveTab(openTabs[numIndex].id);
          setActiveTabData(openTabs[numIndex]);
          scrollToTab(numIndex);
        }
        return;
      }
    }

    if (["Control", "Alt", "Shift", "Meta", "Enter"].includes(e.key)) {
      return;
    }
    let isMac = isMacOS();

    const modifiers = [];
    if (e.ctrlKey) modifiers.push("Ctrl");
    if (e.altKey) {
      if (isMac) {
        modifiers.push("Option");
      } else {
        modifiers.push("Alt");
      }
    }
    if (e.shiftKey) modifiers.push("Shift");
    if (e.metaKey) modifiers.push("Cmd");

    if (!isMac && e.altKey) {
      // Handle number keys (1-9)
      if (e.key >= "1" && e.key <= "9") {
        const numIndex = parseInt(e.key) - 1;
        if (numIndex < openTabs?.length) {
          setActiveTab(openTabs[numIndex].id);
          setActiveTabData(openTabs[numIndex]);
          scrollToTab(numIndex);
        }
        return;
      }
    }

    let key = e.key;
    if (key === " ") key = "Space";

    if (e.altKey) {
      key = e.code.replace("Key", "");
    } else if (key.length === 1) {
      key = key.toUpperCase();
    }

    const newKey = [...modifiers, key].join("+");
    let shortcutId;
    shortcutsKeys?.Tab?.forEach((shortcut) => {
      if (shortcut.key === newKey) {
        shortcutId = shortcut.id;
      }
    });

    switch (shortcutId) {
      case "tab-prev":
        e.preventDefault();
        const newIndex =
          currentIndex === 0 ? openTabs?.length - 1 : currentIndex - 1;
        if (!openTabs[newIndex]) return;
        setActiveTab(openTabs[newIndex].id);
        setActiveTabData(openTabs[newIndex]);
        scrollToTab(newIndex);
        return;
      case "tab-next":
        e.preventDefault();
        const newIndexNext =
          currentIndex === openTabs?.length - 1 ? 0 : currentIndex + 1;
        if (!openTabs[newIndexNext]) return;
        setActiveTab(openTabs[newIndexNext].id);
        setActiveTabData(openTabs[newIndexNext]);
        scrollToTab(newIndexNext);
        return;
      case "tab-close":
        e.preventDefault();
        if (!openTabs[currentIndex]) return;
        handleClose(openTabs[currentIndex].id, currentIndex, e);
        return;
      default:
        break;
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [openTabs, activeTab, shortcutsKeys]);

  const handleDragStart = (e, index) => {
    setIsDragging(true);
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const draggedIndex = parseInt(e.dataTransfer.getData("text/plain"));
    const draggedTab = openTabs[draggedIndex];
    const newTabs = [...openTabs];
    newTabs.splice(draggedIndex, 1);
    newTabs.splice(dropIndex, 0, draggedTab);

    dispatch(setOpenTabs(newTabs));
    localStorage.setItem("openTabs", JSON.stringify(newTabs));

    dispatch(
      create_or_update_opentab_thunk({
        data: newTabs,
        token: token,
      })
    );

    setActiveTab(newTabs[dropIndex].id);
    setActiveTabData(newTabs[dropIndex]);
    setIsDragging(false);
  };

  const handleClose = async (tabId, index, e) => {
    e.stopPropagation();

    let newActiveTabId = null;
    if (openTabs?.length > 1) {
      newActiveTabId =
        openTabs[index + 1]?.id || openTabs[openTabs?.length - 2].id;
    }

    setActiveTab(newActiveTabId);
    setActiveTabData(openTabs[index + 1] || openTabs[openTabs?.length - 2]);
    await new Promise((resolve) => setTimeout(resolve, 50));
    closeTodo(tabId);
  };

  const handleTabClick = (tabId, index) => {
    setActiveTab(tabId);
    setActiveTabData(openTabs[index]);
    scrollToTab(index);
  };

  return (
    <div className="flex flex-col h-full" id="tab-manager">
      <div className="flex bg-gray-900 border-b border-gray-700 overflow-x-auto">
        <div
          ref={tabsContainerRef}
          className="flex overflow-x-auto hide-scrollbar relative"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {openTabs?.map((tab, index) => {
            if (!tab) return null;
            const isActive = activeTab === tab.id;

            return (
              <div
                key={`${tab.id}-${index}`}
                data-tab-index={index}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                style={{ backgroundColor: tab.backgroundColor }}
                className={`
                  group flex items-center min-w-[150px] max-w-[200px]
                  px-4 py-2 relative cursor-pointer border-r border-gray-700
                  ${
                    isActive
                      ? "bg-gray-800 text-white"
                      : "bg-gray-800 text-gray-400 hover:text-gray-200"
                  }
                  ${isDragging ? "cursor-grabbing" : "cursor-pointer"}
                  transition-all duration-200
                `}
              >
                {isActive && (
                  <>
                    <div className="absolute inset-x-0 top-0 h-[2px] bg-cyan-500/50 rounded-full" />
                    <div className="absolute inset-x-0 bottom-0 h-[2px] bg-cyan-500/50 rounded-full" />
                    <div className="absolute inset-y-0 left-0 w-[2px] bg-cyan-500/50 rounded-full" />
                    <div className="absolute inset-y-0 right-0 w-[2px] bg-cyan-500/50 rounded-full" />
                  </>
                )}

                <div
                  className="flex items-center justify-between w-full gap-2"
                  onClick={() => handleTabClick(tab.id, index)}
                >
                  <span className="truncate text-sm">{tab.name}</span>
                  <button
                    onClick={(e) => handleClose(tab.id, index, e)}
                    className={`
                      p-1 rounded-full
                      ${
                        isActive
                          ? "text-gray-100 bg-transparent hover:text-white hover:bg-gray-700"
                          : "text-gray-300 bg-transparent hover:text-gray-300 hover:bg-gray-800"
                      }
                      opacity-0 group-hover:opacity-100 border-none
                      transition-opacity duration-200
                    `}
                  >
                    <IoCloseOutline className="size-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-800">
        {activeTab && (
          <div key={`todo-tab-${activeTab}`}>
            <TodoTab
              todo={openTabs?.find((t) => t.id === activeTab)}
              userId={userId}
              inputValue={tabInputValues[activeTab] || ""}
              onInputChange={(value) => handleInputChange(activeTab, value)}
              toggleComplete={toggleTodoComplete}
              sidebarInputRef={sidebarInputRef}
              todoInputRef={todoInputRef}
              todoEditInputRef={todoEditInputRef}
              tabRenameInputRef={tabRenameInputRef}
              setShortCutFileRef={setShortCutFileRef}
              isComposing={isComposing}
              setIsComposing={setIsComposing}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TabManager;
